import { useCallback } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { MOVE_IN_METER_READS_MAX_ATTEMPTS } from 'config/app-config';
import {
  setMeterRead,
  selectAttemptsCount,
  selectErrors,
  selectIsLoading
} from 'state/meter-reads/move-in-meter-read-slice';
import { useNavigation } from 'hooks/useNavigation';
import { useMeterReadsVerificationError } from './useMeterReadsVerificationError';
import { useMeterReadsPayload } from 'components/direct-sales/meter-reads/hooks/useMeterReadsPayload';
import { sendMoveInMeterReadThunk } from 'state/meter-reads/move-in-meter-read-thunk';
import { MeterReadAgentHandOffError } from 'errors/MeterReadErrors';

export const useMeterRead = () => {
  const dispatch = useDispatch();
  const errors = useSelector(selectErrors, shallowEqual);
  const isLoading = useSelector(selectIsLoading, shallowEqual);
  const attemptsCount = useSelector(selectAttemptsCount, shallowEqual);
  const { eventTypes, next } = useNavigation();
  const payload = useMeterReadsPayload();
  const handleError = useMeterReadsVerificationError();
  const hasCta = !errors?.some(
    (error) => error.name === MeterReadAgentHandOffError.name
  );

  const hasReachedMaxAttemps =
    attemptsCount >= MOVE_IN_METER_READS_MAX_ATTEMPTS;

  const handleReadingChange = useCallback(
    (event) => {
      const { name, processedValue: value } = event.target;

      dispatch(
        setMeterRead({
          name,
          value
        })
      );
    },
    [dispatch]
  );

  const handleSubmit = useCallback(async () => {
    try {
      payload && (await dispatch(sendMoveInMeterReadThunk(payload))); // no meter reads proceed to next step
      next({ eventType: eventTypes.NEXT });
    } catch (errors) {
      const e = handleError(errors);
      e && console.error(e);

      if (hasReachedMaxAttemps) {
        next({ eventType: eventTypes.NEXT });
      }
    }
  }, [dispatch, payload, next, eventTypes, handleError, hasReachedMaxAttemps]);

  return {
    hasReachedMaxAttemps,
    hasCta,
    isLoading,
    errors,
    handleReadingChange,
    handleSubmit
  };
};
